import {Component, EventEmitter, Input, Output, ViewChild, forwardRef} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Client} from '@app/layout/admin/services/api/client-interfaces';
import {I18nMessage} from '@app/shared/api/model/i18n';
import {SiteId, SiteInfo} from '@app/shared/services/api/access-sites.interface';
import {CollectionUtils} from '@app/shared/util/collections/collection-utils';
import {ObjectUtils} from '@app/shared/util/general/object-utils';
import {IconName} from '@fortawesome/fontawesome-svg-core';
import {SiteSelectComponent} from '../site-select/site-select.component';

type SiteSelectInputOutput = SiteInfo | SiteId | SiteInfo[] | SiteId[];
type SiteSelectValue = SiteId;

@Component({
  selector: 'app-site-select-with-single-and-multi',
  templateUrl: './site-select-with-single-and-multi.component.html',
  styleUrls: ['./site-select-with-single-and-multi.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SiteSelectWithSingleAndMultiComponent), multi: true}
  ],
  standalone: false
})
export class SiteSelectWithSingleAndMultiComponent implements ControlValueAccessor {
  readonly sitesItemName: I18nMessage = {messageKey: 'Sites'};
  readonly singleSiteItemName: I18nMessage = {messageKey: 'Site'};

  @Input() sites: SiteInfo[];
  @Input() bindLabel = 'site_name';
  @Input() bindId = true;
  @Input() multiSelect = false;
  @Input() secondaryButtonIcon: IconName = 'clone';
  @Input() secondaryAddButtonText: I18nMessage = {messageKey: 'Compare'};
  @Input() secondarySelectPlaceholder: I18nMessage = {messageKey: 'Add sites to compare'};
  @Output() sitesLoaded: EventEmitter<SiteInfo[]> = new EventEmitter();
  @Output() clientsLoaded: EventEmitter<Client[]> = new EventEmitter();
  @ViewChild('secondarySiteSelect', {static: false}) secondarySiteSelect: SiteSelectComponent;
  
  sitesById: Map<SiteSelectValue, SiteInfo>;
  secondaryControlEditMode = false;
  primarySiteId: SiteSelectValue = null;
  secondarySiteIds: SiteSelectValue[];
  loadedSites: SiteInfo[];
  disabled = false;

  onChangedFn = (_value: SiteSelectInputOutput): void => {};
  onTouchedFn = (): void => {};

  writeValue(value: SiteSelectInputOutput): void {
    const valueIsArray = Array.isArray(value);
    if (value != null && this.multiSelect !== valueIsArray) {
      throw new Error('Control input `multiSelect` is "' + this.multiSelect + '" but value "' + value + (valueIsArray ? '" is an array' : '" is not an array "'));
    }
    const sampleValue = valueIsArray ? value?.[0] : value;
    if (sampleValue != null && (this.bindId ? typeof sampleValue !== 'number' : sampleValue['site_id'] == null)) {
      throw new Error('Control input `bindId` is "' + this.bindId + '" but value "' + sampleValue + '" has type of "' + typeof sampleValue + '"');
    }
    const valueArray = valueIsArray ? value : (value != null ? [value] : null);
    const siteIds = this.bindId ? <number[]>valueArray : (<SiteInfo[]>valueArray)?.map(value => value.site_id);
    this.primarySiteId = siteIds?.[0] ?? null;
    const siteIdsTail: number[] | null = siteIds?.slice(1);
    this.secondarySiteIds = siteIdsTail?.length ? siteIdsTail : null;
  }

  registerOnChange(fn: any): void {
    this.onChangedFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  updatedSecondarySiteListEditMode(editMode: boolean): void {
    this.secondaryControlEditMode = editMode;
    if (editMode) {
      // Need to wait for secondary site control to appear and be available as reference on `secondarySiteSelect`.
      setTimeout(() => this.secondarySiteSelect?.open());
    }
  }

  updatePrimarySelectedSite(newSelectedSite: SiteSelectValue): void {
    this.primarySiteId = newSelectedSite;
    this.updateSecondarySelectedSites(null);
  }

  updateSecondarySelectedSites(newSelectedSites: SiteSelectValue[]): void {
    this.secondarySiteIds = newSelectedSites;
    this.onChangedFn(this.selectedSites);
  }

  updateSiteOptions(sites: SiteInfo[]): void {
    /** Because `sitesLoaded` emits immediately when sites are already cached, it changes `sitesById.size` immediately as well
     * and throws ExpressionChangedAfterItHasBeenCheckedError. setTimeout makes it not be set on the same changeDetection round. */
    setTimeout(() => {
      this.loadedSites = sites;
      this.sitesById = CollectionUtils.indexBy(sites, 'site_id');
      this.sitesLoaded.emit(sites);
    });
  }

  updateClients(clients: Client[]): void {
    this.clientsLoaded.emit(clients);
  }

  get secondarySiteNames(): string[] {
    return this.sitesById?.size > 0 && this.secondarySiteIds?.length > 0
      ? this.secondarySiteIds.map(siteId => this.sitesById.get(siteId)?.site_name)
      : null;
  }

  private get selectedSites(): SiteSelectInputOutput {
    const siteIds: SiteSelectValue | SiteSelectValue[] = this.multiSelect ? this.getSelectedSiteIds() : this.primarySiteId;
    return this.bindId
      ? siteIds
      : ObjectUtils.mapObjectOrArray<SiteSelectValue, SiteInfo>(siteIds, siteId => this.sitesById.get(siteId));
  }

  private getSelectedSiteIds(): number[] {
    return [...new Set([this.primarySiteId, ...(this.secondarySiteIds ?? [])])];
  }
}
